import {
  invalidateSessionsQuery,
  useCancelSessionsQuery,
} from '@/hooks/query/useQueryCombinedSessions';
import { deleteEmptySessions } from '@/pages/api/scribeAPIV2';
import { logger } from '@/utils/logger/logger';
import { toast } from '@heidi/design-system/toast';
import { useTranslations } from '@heidi/runtime/i18n';
import { GET_SESSION_BY_ID } from '@heidi/session/contracts';
import { useMutation, useQueryClient } from '@tanstack/react-query';

export function useMutateDeleteEmptySessions() {
  const { t } = useTranslations();
  const queryClient = useQueryClient();
  const { cancelSessionsQuery } = useCancelSessionsQuery();

  return useMutation({
    mutationKey: ['DeleteEmptySessions'],
    mutationFn: async () => {
      return await deleteEmptySessions();
    },

    onMutate: async () => {
      await cancelSessionsQuery();
    },

    onSuccess: async ({ data }) => {
      await invalidateSessionsQuery();

      // Invalidate all individual session queries since we don't know which sessions were deleted
      if (data.num_deleted > 0) {
        void queryClient.invalidateQueries({
          queryKey: [GET_SESSION_BY_ID],
        });
      }

      if (data.num_deleted > 0) {
        toast({
          tone: 'success',
          title:
            data.num_deleted === 1
              ? t('session.emptySessionDeleted', { count: data.num_deleted })
              : t('session.emptySessionsDeleted', { count: data.num_deleted }),
        });
      } else {
        toast({
          tone: 'default',
          title: t('session.noEmptySessionsToDelete'),
        });
      }
    },

    onError: (error) => {
      logger.error('Error:', error);
      toast({
        tone: 'destructive',
        title: 'Backend Error',
        description: 'useMutateDeleteEmptySessions',
      });
    },
  });
}
